import { useCallback, useMemo } from 'react';
import { PixelRatio, useWindowDimensions } from 'react-native';

import {
  type FontScaleCategory,
  type TypographyVariant,
  getFontSize as getBaseFontSize,
  getTypographyStyle,
} from '@/constants/typography';

const categorizeFontScale = (scale: number): FontScaleCategory => {
  if (scale <= 0.9) {
    return 'smaller';
  }

  if (scale >= 1.15) {
    return 'larger';
  }

  return 'standard';
};

export const useFontScale = () => {
  const { fontScale } = useWindowDimensions();
  const fallbackScale = PixelRatio.getFontScale();
  const effectiveScale =
    typeof fontScale === 'number' && Number.isFinite(fontScale) && fontScale > 0
      ? fontScale
      : fallbackScale;

  const category = useMemo(
    () => categorizeFontScale(effectiveScale),
    [effectiveScale]
  );

  const getScaledFontSize = useCallback(
    (variant: TypographyVariant) => getBaseFontSize(variant, category),
    [category]
  );

  const getScaledTypography = useCallback(
    (variant: TypographyVariant) => getTypographyStyle(variant, category),
    [category]
  );

  return {
    category,
    fontScale: effectiveScale,
    getScaledFontSize,
    getScaledTypography,
  };
};
